import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Store,
  Gavel,
  Tag,
  CheckCircle2,
  Loader2,
  ArrowRight,
  TrendingUp,
  Clock,
  DollarSign,
  ShieldCheck,
  AlertTriangle,
  Sparkles,
  ChevronDown,
} from 'lucide-react';
import { useHapticFeedback } from '@/hooks/useHapticFeedback';
import { useNotify } from '@/context/NotificationContext';
import { v2Api } from '@/utils/v2Api';
import { usernameMarketApi, apiFetch } from '@/utils/api';
import { useStore } from '@/store/useStore';
import { useTranslation } from 'react-i18next';

// ─── Types ───────────────────────────────────────────────────────────────────
interface PushMarketModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id?: number;
    nft_address?: string;
    nft_name?: string;
    image_url?: string;
    collection_name?: string;
    is_ownership?: boolean;
    type?: string;
    category?: string;
    attributes?: any;
    ownership_source?: string;
    session_phone?: string;
    session_name?: string;
    session_avatar?: string;
    asset_id?: string | number;
    username?: string;
  } | null;
  /** Called on successful listing so the parent can refresh state */
  onSuccess?: (listingResult: any) => void;
}

type ListingType = 'direct' | 'auction' | 'offers';

const DURATION_OPTIONS = [
  { label: '12h', hours: 12 },
  { label: '24h', hours: 24 },
  { label: '3d', hours: 72 },
  { label: '7d', hours: 168 },
];

// ─── Helpers ─────────────────────────────────────────────────────────────────
const pfkGlassStyle = `
  .pfk-glass {
    background-color: rgba(28,28,30,.55);
    -webkit-backdrop-filter: blur(8px) saturate(140%);
    backdrop-filter: blur(8px) saturate(140%);
    box-shadow:
      inset 0 0 0 1px rgba(255,255,255,.05),
      inset 1.8px 3px 0px -2px rgba(255,255,255,.27),
      inset -2px -2px 0px -2px rgba(255,255,255,.22),
      inset -3px -8px 1px -6px rgba(255,255,255,.16),
      inset -.3px -1px 4px 0px rgba(0,0,0,.24),
      inset -1.5px 2.5px 0px -2px rgba(0,0,0,.4),
      inset 0px 3px 4px -2px rgba(0,0,0,.4),
      0px 1px 5px 0px rgba(0,0,0,.2),
      0px 6px 16px 0px rgba(0,0,0,.16);
    border: none;
  }
  .pfk-input {
    background-color: rgba(28,28,30,.55);
    -webkit-backdrop-filter: blur(8px) saturate(140%);
    backdrop-filter: blur(8px) saturate(140%);
    box-shadow:
      inset 0 0 0 1px rgba(255,255,255,.06),
      inset 1.8px 3px 0px -2px rgba(255,255,255,.25),
      inset -2px -2px 0px -2px rgba(255,255,255,.2),
      0px 1px 5px 0px rgba(0,0,0,.2),
      0px 6px 16px 0px rgba(0,0,0,.16);
    transition: box-shadow .2s;
  }
  .pfk-input:focus-within {
    box-shadow:
      inset 0 0 0 1px rgba(51,144,236,.55),
      inset 1.8px 3px 0px -2px rgba(255,255,255,.25),
      0px 1px 5px 0px rgba(0,0,0,.2),
      0px 6px 16px 0px rgba(0,0,0,.16);
  }
`;

/** Extract a clean Telegram username from an inventory item */
function extractUsername(item: PushMarketModalProps['item']): string {
  if (!item) return '';
  if (item.username) return String(item.username).replace(/^@/, '').trim();
  // nft_name is stored as "@username"
  if (item.nft_name) return item.nft_name.replace(/^@/, '').trim();
  // nft_address stored as "ownership_username"
  if (item.nft_address) {
    const addr = String(item.nft_address);
    if (addr.startsWith('ownership_')) return addr.replace('ownership_', '').trim();
    return addr.replace(/^@/, '').trim();
  }
  return '';
}

/** Determine if the inventory item is an ownership (P2P) username (not an NFT) */
function isOwnershipItem(item: PushMarketModalProps['item']): boolean {
  if (!item) return false;
  let attrs: any = {};
  if (item.attributes) {
    try {
      attrs = typeof item.attributes === 'string' ? JSON.parse(item.attributes) : item.attributes;
    } catch (_) {}
  }
  return (
    item.is_ownership === true ||
    item.type === 'ownership' ||
    item.category === 'ownership' ||
    attrs.is_ownership === true ||
    attrs.type === 'ownership' ||
    attrs.category === 'ownership' ||
    (item.nft_address != null && String(item.nft_address).startsWith('ownership_')) ||
    (item.collection_name != null && String(item.collection_name).toLowerCase().includes('p2p'))
  );
}

/** Determine if the item is an app (push-bot session) username — listed via /push-usernames/action,
 *  so the store shows the session's info and purchases transfer instantly via Push. */
function isAppSessionItem(item: PushMarketModalProps['item']): boolean {
  if (!item) return false;
  let attrs: any = {};
  if (item.attributes) {
    try {
      attrs = typeof item.attributes === 'string' ? JSON.parse(item.attributes) : item.attributes;
    } catch (_) {}
  }
  return (
    item.ownership_source === 'app' ||
    attrs.ownership_source === 'app' ||
    (item.collection_name != null && String(item.collection_name).toLowerCase() === 'push session username') ||
    (attrs.session_phone != null && String(attrs.session_phone).length > 0)
  );
}

// ─── Sub-Components ───────────────────────────────────────────────────────────

const TabButton: React.FC<{
  active: boolean;
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  accent: string;
}> = ({ active, onClick, icon, label, accent }) => (
  <button
    onClick={onClick}
    className={`flex-1 flex flex-col items-center gap-1 py-2.5 rounded-2xl transition-all duration-200 active:scale-95 ${
      active
        ? `${accent} text-white shadow-lg`
        : 'pfk-glass text-white/50 hover:text-white/80'
    }`}
  >
    <span className="w-5 h-5 flex items-center justify-center">{icon}</span>
    <span className="text-[10px] font-black uppercase tracking-widest">{label}</span>
  </button>
);

const SummaryCard: React.FC<{
  username: string;
  imageUrl?: string;
  collectionName?: string;
  listingType: ListingType;
  price: string;
  durationHours: number;
}> = ({ username, imageUrl, collectionName, listingType, price, durationHours }) => {
  const { t } = useTranslation('store');
  const typeLabel = listingType === 'direct' ? t('directSale') : listingType === 'auction' ? t('auction') : t('acceptsOffers');
  const typeColor = listingType === 'direct' ? 'text-blue-300' : listingType === 'auction' ? 'text-amber-300' : 'text-violet-300';

  return (
    <div className="pfk-glass rounded-2xl p-3 flex items-center gap-3">
      {/* Avatar */}
      <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-cyan-400/25 to-emerald-400/25 border border-white/20 flex items-center justify-center shrink-0 overflow-hidden">
        {imageUrl ? (
          <img src={imageUrl} alt={username} className="w-full h-full object-cover rounded-2xl" />
        ) : (
          <span className="font-extrabold text-cyan-300 text-base">@</span>
        )}
      </div>
      {/* Info */}
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className="text-xs font-black text-white font-mono truncate">@{username}</span>
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse shrink-0" />
        </div>
        <p className="text-[10px] text-[#8e8e93] font-medium truncate">{collectionName || t('telegramUsernameP2P')}</p>
      </div>
      {/* Price pill */}
      <div className="shrink-0 text-right">
        <p className={`text-[10px] font-black uppercase tracking-wider ${typeColor}`}>{typeLabel}</p>
        {price && Number(price) > 0 && (
          <p className="text-xs font-extrabold text-white">{price} <span className="text-white/50 font-medium text-[10px]">GRAM</span></p>
        )}
        {listingType === 'auction' && (
          <p className="text-[9px] text-white/40 font-medium">{DURATION_OPTIONS.find(d => d.hours === durationHours)?.label ?? `${durationHours}h`}</p>
        )}
      </div>
    </div>
  );
};

// ─── Main Modal ───────────────────────────────────────────────────────────────
export const PushMarketModal: React.FC<PushMarketModalProps> = ({
  isOpen,
  onClose,
  item,
  onSuccess,
}) => {
  const { impactOccurred, notificationOccurred } = useHapticFeedback();
  const { showNotification } = useNotify();
  const { t } = useTranslation('store');
  const { t: tc } = useTranslation('common');

  const [listingType, setListingType] = useState<ListingType>('direct');
  const [price, setPrice] = useState('');
  const [minIncrement, setMinIncrement] = useState('1');
  const [durationHours, setDurationHours] = useState(24);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submitMsg, setSubmitMsg] = useState('');
  const [alreadyListed, setAlreadyListed] = useState<null | { type: string; price: number }>(null);

  const { user } = useStore();
  const username = extractUsername(item);
  const isOwnership = isOwnershipItem(item);
  const isAppSession = isAppSessionItem(item);

  // ─── Already-listed guard: if this username is already live on the market,
  //     change the button so the user won't re-push it a second time. ───
  useEffect(() => {
    setAlreadyListed(null);
    if (!isOpen || !isOwnership || !username) return;
    let alive = true;
    const ACTIVE = new Set(['active', 'listed', 'for_sale', 'on_sale']);
    const target = username.trim().toLowerCase();

    (async () => {
      const candidates: { username: string; type: string; status: string; price: number }[] = [];
      const uid = user?.telegram_id;
      if (uid) {
        try {
          const v2r = await v2Api.userListings(uid);
          if (v2r?.success && Array.isArray(v2r.listings)) {
            v2r.listings.forEach((l: any) => {
              candidates.push({
                username: String(l.username || '').toLowerCase(),
                type: String(l.listing_type || l.sale_type || '').toLowerCase(),
                status: String(l.status || l.store_status || '').toLowerCase(),
                price: Number(l.sale_price ?? l.price ?? 0),
              });
            });
          }
        } catch (_) {}
      }
      try {
        const um = await usernameMarketApi.my();
        if (um?.ok) {
          const r = await um.json();
          const lists = [...(r?.store_listings || []), ...(r?.auction_listings || [])];
          lists.forEach((l: any) => {
            candidates.push({
              username: String(l.username || '').toLowerCase(),
              type: String(l.sale_type || '').toLowerCase(),
              status: String(l.listing_status || '').toLowerCase(),
              price: Number(l.buy_now_price ?? l.ask_price ?? l.start_price ?? l.price ?? 0),
            });
          });
        }
      } catch (_) {}

      // App (push session) items live under the session's own profile — check the raw push feed.
      if (isAppSession) {
        try {
          const pRes = await apiFetch('/api/admin/push-usernames');
          if (pRes.ok) {
            const pData = await pRes.json();
            const pItem = (pData?.usernames || []).find((u: any) =>
              String(u.username || u.rawHandle || '').toLowerCase() === target
            );
            if (pItem) {
              const st = String(pItem.status || pItem.pool_status || '').toLowerCase();
              if (st === 'listed' || st === 'store' || st === 'for_sale' || st === 'on_sale') {
                candidates.push({
                  username: target,
                  type: 'store',
                  status: 'listed',
                  price: Number(pItem.listingPrice ?? pItem.ask_price ?? pItem.price ?? 0),
                });
              } else if (st === 'auction' || st === 'auctioned') {
                candidates.push({ username: target, type: 'auction', status: 'listed', price: Number(pItem.price ?? pItem.start_price ?? 0) });
              } else if (st === 'offers' || st === 'offer') {
                candidates.push({ username: target, type: 'offer', status: 'listed', price: Number(pItem.price ?? pItem.min_offer ?? 0) });
              }
            }
          }
        } catch (_) {}
      }

      const hit = candidates.find(c => c.username === target && ACTIVE.has(c.status));
      if (alive && hit) setAlreadyListed(hit ? { type: hit.type || 'store', price: hit.price } : null);
    })();

    return () => { alive = false; };
  }, [isOpen, isOwnership, isAppSession, username, user?.telegram_id]);

  const handleClose = useCallback(() => {
    if (isSubmitting) return;
    impactOccurred('light');
    // Reset state before closing
    setListingType('direct');
    setPrice('');
    setMinIncrement('1');
    setDurationHours(24);
    setSubmitStatus('idle');
    setSubmitMsg('');
    onClose();
  }, [isSubmitting, impactOccurred, onClose]);

  const handleDragEnd = (_: any, info: any) => {
    if (info.offset.y > 30 || info.velocity.y > 60) handleClose();
  };

  const handleTabChange = (type: ListingType) => {
    impactOccurred('light');
    setListingType(type);
    setSubmitStatus('idle');
    setSubmitMsg('');
  };

  const validate = (): string | null => {
    if (alreadyListed) return t('alreadyListedValidation');
    if (isOwnership && !username) return t('noUsernameFound');
    if (!isOwnership && !item?.id && !item?.nft_address) return t('nftIdentifierNotFound');
    const p = parseFloat(price);
    if (!price || isNaN(p) || p <= 0) return t('invalidPrice');
    if (listingType === 'auction') {
      const inc = parseFloat(minIncrement);
      if (isNaN(inc) || inc <= 0) return t('invalidMinIncrement');
    }
    return null;
  };

  const handleSubmit = async () => {
    const validationErr = validate();
    if (validationErr) {
      notificationOccurred('warning');
      setSubmitStatus('error');
      setSubmitMsg(validationErr);
      return;
    }

    impactOccurred('heavy');
    setIsSubmitting(true);
    setSubmitStatus('idle');
    setSubmitMsg('');

    try {
      const apiListingType = listingType === 'direct' ? 'store' : listingType === 'offers' ? 'offer' : 'auction';
      let result: any;

      if (isOwnership && isAppSession) {
        // App-owned push session: publish through the push-bot sync so the store shows
        // the session's own info and buyers get instant Push transfer (owned by the session).
        const r = await apiFetch('/api/admin/push-usernames/action', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username,
            actionType: listingType === 'auction' ? 'auction' : listingType === 'offers' ? 'offers' : 'price',
            price: parseFloat(price),
            duration: listingType === 'auction' ? durationHours : undefined,
            minIncrement: listingType === 'auction' ? parseFloat(minIncrement) || 1 : undefined,
            offersEnabled: listingType === 'offers',
            minOffer: listingType === 'offers' ? parseFloat(price) || undefined : undefined,
            ownershipSource: 'app',
          }),
        });
        const j = await r.json().catch(() => ({}));
        if (!r.ok || !j.success) {
          throw new Error(j.error || `Publish rejected (${r.status})`);
        }
        result = j;
      } else if (isOwnership) {
        // P2P Ownership username listing
        result = await v2Api.createUsernameListing({
          username,
          listingType: apiListingType as 'store' | 'auction' | 'offer',
          price: parseFloat(price),
          durationHours: listingType === 'auction' ? durationHours : undefined,
          minIncrement: listingType === 'auction' ? parseFloat(minIncrement) || 1 : undefined,
        });
      } else {
        // NFT listing via generic listing endpoint
        result = await v2Api.createListing({
          nftId: item?.id,
          nftAddress: item?.nft_address,
          nftName: item?.nft_name,
          price: parseFloat(price),
          listingType: apiListingType,
          currency: 'GRAM',
          durationHours: listingType === 'auction' ? durationHours : undefined,
          minIncrement: listingType === 'auction' ? parseFloat(minIncrement) || 1 : undefined,
        });
      }

      const displayLabel = isOwnership ? `@${username}` : (item?.nft_name || 'NFT');
      if (result.success || result.listing || result.auction) {
        notificationOccurred('success');
        setSubmitStatus('success');
        setSubmitMsg(
          isAppSession
            ? t('sessionPublished', { username })
            : t('listedSuccess', { label: displayLabel })
        );
        showNotification(t('listedTitle'), isAppSession ? t('sessionLive', { username }) : t('nowLive', { label: displayLabel }), 'success');
        onSuccess?.(result);
        // Auto-close after brief success display
        setTimeout(() => handleClose(), 1800);
      } else {
        notificationOccurred('error');
        setSubmitStatus('error');
        setSubmitMsg(result.error || t('listingFailed'));
      }
    } catch (e: any) {
      notificationOccurred('error');
      setSubmitStatus('error');
      setSubmitMsg(e?.message || t('networkErrorRetry'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-xl flex items-end sm:items-center justify-center p-0 sm:p-4">
          <style>{pfkGlassStyle}</style>

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            onClick={handleClose}
            className="absolute inset-0 z-0"
          />

          {/* Sheet */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%', transition: { duration: 0.18, ease: [0.32, 0, 0.67, 0] } }}
            transition={{ type: 'spring', damping: 26, stiffness: 420, mass: 0.4 }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            dragElastic={{ top: 0, bottom: 1 }}
            dragSnapToOrigin
            onDragEnd={handleDragEnd}
            className="w-full max-w-md pfk-glass rounded-t-[32px] sm:rounded-[32px] px-4 pt-2.5 pb-6 shadow-2xl relative z-10 overflow-hidden flex flex-col max-h-[92vh] overscroll-contain touch-pan-y"
          >
            {/* Top handle */}
            <div
              onClick={handleClose}
              className="w-10 h-1 rounded-full bg-white/20 mx-auto mb-3 cursor-pointer hover:bg-white/40 transition-all shrink-0"
            />

            {/* Top gloss line */}
            <div className="absolute top-0 inset-x-0 h-[1px] bg-gradient-to-r from-transparent via-white/30 to-transparent pointer-events-none" />

            {/* Header */}
            <div className="flex items-center justify-between mb-4 px-1 shrink-0">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-2xl pfk-glass flex items-center justify-center text-blue-400">
                  <Store size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-extrabold text-white tracking-tight flex items-center gap-1.5">
                    {t('pushToMarket')}
                    <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded-full pfk-glass ${
                      isOwnership ? 'text-blue-300' : 'text-amber-300'
                    }`}>
                      {isOwnership ? t('ownershipType') : 'NFT'}
                    </span>
                  </h3>
                  <p className="text-[10px] text-[#8e8e93] font-medium mt-0.5">
                    {isAppSession
                      ? t('pushSessionHint')
                      : isOwnership ? t('listUsernameHint') : t('listNftHint')}
                  </p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className="w-7 h-7 rounded-full pfk-glass flex items-center justify-center text-white/50 hover:text-white transition-colors"
              >
                <X size={14} />
              </button>
            </div>

            {/* Scrollable content */}
            <div className="overflow-y-auto no-scrollbar space-y-3 pr-0.5 pb-1 flex-1">
              {/* Item Summary Card */}
              <SummaryCard
                username={isOwnership ? username : (item?.nft_name?.replace(/^@/, '') || item?.nft_address || 'NFT')}
                imageUrl={item?.image_url}
                collectionName={item?.collection_name || (isOwnership ? t('telegramUsernameP2P') : t('tonUserNft'))}
                listingType={listingType}
                price={price}
                durationHours={durationHours}
              />

              {/* Already-listed guard banner */}
              {alreadyListed && submitStatus !== 'success' && (
                <motion.div
                  initial={{ opacity: 0, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="pfk-glass rounded-2xl p-3 flex items-start gap-3 border border-amber-500/25"
                >
                  <div className="w-8 h-8 rounded-xl bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
                    <Store size={15} className="text-amber-400" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-extrabold text-amber-300">{t('alreadyListedBanner')}</p>
                    <p className="text-[10px] text-amber-200/60 font-medium leading-relaxed">
                      {alreadyListed.type === 'auction'
                        ? (alreadyListed.price > 0 ? t('alreadyListedAuction', { username, price: alreadyListed.price }) : t('alreadyListedAuctionNoPrice', { username }))
                        : alreadyListed.type === 'offer'
                        ? (alreadyListed.price > 0 ? t('alreadyListedOffers', { username, price: alreadyListed.price }) : t('alreadyListedOffersNoPrice', { username }))
                        : (alreadyListed.price > 0 ? t('alreadyListedDirect', { username, price: alreadyListed.price }) : t('alreadyListedDirectNoPrice', { username }))
                      }
                    </p>
                  </div>
                  <CheckCircle2 size={16} className="text-amber-400 shrink-0 mt-0.5" />
                </motion.div>
              )}

                  {/* Listing Type Tabs */}
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2 px-1">{t('listingType')}</p>
                    <div className="flex gap-2">
                      <TabButton
                        active={listingType === 'direct'}
                        onClick={() => handleTabChange('direct')}
                        icon={<Store size={14} />}
                        label={t('direct')}
                        accent="bg-gradient-to-br from-blue-600/80 to-blue-500/60"
                      />
                      <TabButton
                        active={listingType === 'auction'}
                        onClick={() => handleTabChange('auction')}
                        icon={<Gavel size={14} />}
                        label={t('auction')}
                        accent="bg-gradient-to-br from-amber-600/80 to-orange-500/60"
                      />
                      <TabButton
                        active={listingType === 'offers'}
                        onClick={() => handleTabChange('offers')}
                        icon={<Tag size={14} />}
                        label={t('offers')}
                        accent="bg-gradient-to-br from-violet-600/80 to-purple-500/60"
                      />
                    </div>
                  </div>

                  {/* ─── DIRECT SALE Fields ─── */}
                  {listingType === 'direct' && (
                    <div className="pfk-glass rounded-2xl p-3 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Store size={13} className="text-blue-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-white/60">{t('directSaleFixed')}</span>
                      </div>
                      {/* Price input */}
                      <div>
                        <label className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                          {t('salePrice')}
                        </label>
                        <div className="pfk-input rounded-xl px-3 h-11 flex items-center gap-2">
                          <DollarSign size={14} className="text-blue-400 shrink-0" />
                          <input
                            type="number"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={price}
                            onChange={(e) => { setPrice(e.target.value); setSubmitStatus('idle'); }}
                            className="bg-transparent text-sm text-white placeholder-white/30 font-mono font-semibold outline-none w-full"
                          />
                          <span className="text-[10px] font-bold text-white/40 shrink-0">GRAM</span>
                        </div>
                      </div>
                      <div className="pfk-glass rounded-xl p-2.5 flex items-center gap-2">
                        <ShieldCheck size={12} className="text-blue-400 shrink-0" />
                        <p className="text-[9px] text-white/60 font-medium leading-tight">
                          {isAppSession
                            ? t('instantTransferHint')
                            : t('immediatePaymentHint')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ─── AUCTION Fields ─── */}
                  {listingType === 'auction' && (
                    <div className="pfk-glass rounded-2xl p-3 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Gavel size={13} className="text-amber-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-white/60">{t('auctionHighestBid')}</span>
                      </div>
                      {/* Starting Price */}
                      <div>
                        <label className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                          {t('startingPrice')}
                        </label>
                        <div className="pfk-input rounded-xl px-3 h-11 flex items-center gap-2">
                          <TrendingUp size={14} className="text-amber-400 shrink-0" />
                          <input
                            type="number"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={price}
                            onChange={(e) => { setPrice(e.target.value); setSubmitStatus('idle'); }}
                            className="bg-transparent text-sm text-white placeholder-white/30 font-mono font-semibold outline-none w-full"
                          />
                          <span className="text-[10px] font-bold text-white/40 shrink-0">GRAM</span>
                        </div>
                      </div>
                      {/* Min Increment */}
                      <div>
                        <label className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                          {t('minBidIncrement')}
                        </label>
                        <div className="pfk-input rounded-xl px-3 h-11 flex items-center gap-2">
                          <ChevronDown size={14} className="text-amber-400 shrink-0" />
                          <input
                            type="number"
                            inputMode="decimal"
                            placeholder="1"
                            value={minIncrement}
                            onChange={(e) => setMinIncrement(e.target.value)}
                            className="bg-transparent text-sm text-white placeholder-white/30 font-mono font-semibold outline-none w-full"
                          />
                          <span className="text-[10px] font-bold text-white/40 shrink-0">GRAM</span>
                        </div>
                      </div>
                      {/* Duration Selector */}
                      <div>
                        <label className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                          {t('auctionDuration')}
                        </label>
                        <div className="flex gap-2">
                          {DURATION_OPTIONS.map((opt) => (
                            <button
                              key={opt.hours}
                              onClick={() => { impactOccurred('light'); setDurationHours(opt.hours); }}
                              className={`flex-1 py-2 rounded-xl font-black text-[11px] transition-all active:scale-95 ${
                                durationHours === opt.hours
                                  ? 'bg-amber-500/80 text-white shadow-lg'
                                  : 'pfk-glass text-white/50 hover:text-white/80'
                              }`}
                            >
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="pfk-glass rounded-xl p-2.5 flex items-center gap-2">
                        <Clock size={12} className="text-amber-400 shrink-0" />
                        <p className="text-[9px] text-white/60 font-medium leading-tight">
                          {t('highestBidderHint')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* ─── OFFERS Fields ─── */}
                  {listingType === 'offers' && (
                    <div className="pfk-glass rounded-2xl p-3 space-y-3">
                      <div className="flex items-center gap-2 mb-1">
                        <Tag size={13} className="text-violet-400" />
                        <span className="text-[10px] font-black uppercase tracking-widest text-white/60">{t('acceptsOffersYouDecide')}</span>
                      </div>
                      {/* Floor Price */}
                      <div>
                        <label className="text-[10px] font-bold text-white/50 uppercase tracking-wider block mb-1.5">
                          {t('floorPrice')}
                        </label>
                        <div className="pfk-input rounded-xl px-3 h-11 flex items-center gap-2">
                          <Tag size={14} className="text-violet-400 shrink-0" />
                          <input
                            type="number"
                            inputMode="decimal"
                            placeholder="0.00"
                            value={price}
                            onChange={(e) => { setPrice(e.target.value); setSubmitStatus('idle'); }}
                            className="bg-transparent text-sm text-white placeholder-white/30 font-mono font-semibold outline-none w-full"
                          />
                          <span className="text-[10px] font-bold text-white/40 shrink-0">GRAM</span>
                        </div>
                      </div>
                      <div className="pfk-glass rounded-xl p-2.5 flex items-center gap-2">
                        <ShieldCheck size={12} className="text-violet-400 shrink-0" />
                        <p className="text-[9px] text-white/60 font-medium leading-tight">
                          {t('offersFlowHint')}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Status Feedback */}
                  <AnimatePresence mode="wait">
                    {submitStatus === 'success' && (
                      <motion.div
                        key="success"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="pfk-glass rounded-2xl p-3 flex items-center gap-3 border border-emerald-500/20"
                      >
                        <div className="w-8 h-8 rounded-xl bg-emerald-500/20 flex items-center justify-center shrink-0">
                          <CheckCircle2 size={16} className="text-emerald-400" />
                        </div>
                        <div>
                          <p className="text-xs font-extrabold text-emerald-400">{t('listedSuccessTitle')}</p>
                          <p className="text-[10px] text-emerald-200/70 font-medium">{submitMsg}</p>
                        </div>
                        <Sparkles size={16} className="text-emerald-400 ml-auto shrink-0 animate-pulse" />
                      </motion.div>
                    )}
                    {submitStatus === 'error' && (
                      <motion.div
                        key="error"
                        initial={{ opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        className="bg-[#2d191d] border border-red-500/20 rounded-2xl p-3 flex items-start gap-3"
                      >
                        <div className="w-8 h-8 rounded-xl bg-red-500/20 flex items-center justify-center shrink-0 mt-0.5">
                          <AlertTriangle size={14} className="text-red-400" />
                        </div>
                        <div>
                          <p className="text-xs font-extrabold text-red-400">{t('failedTitle')}</p>
                          <p className="text-[10px] text-red-200/70 font-medium leading-relaxed">{submitMsg}</p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
            </div>

            {/* Bottom Action Button */}
            <div className="pt-3 shrink-0">
              {submitStatus === 'success' ? (
                <button
                  onClick={handleClose}
                  className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 font-extrabold text-xs text-white tracking-wide flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-xl"
                >
                  <CheckCircle2 size={16} />
                  <span>{tc('done')}</span>
                </button>
              ) : alreadyListed ? (
                <button
                  onClick={handleClose}
                  className="w-full h-11 rounded-full bg-amber-500/20 text-amber-300 font-extrabold text-xs tracking-wide flex items-center justify-center gap-2 shadow-xl cursor-pointer hover:bg-amber-500/30 transition-all"
                >
                  <Store size={15} />
                  <span>
                    {t('alreadyListed')}
                    {alreadyListed.type === 'auction' ? t('suffixAuction') : alreadyListed.type === 'offer' ? t('suffixOffers') : t('suffixStore')}
                  </span>
                </button>
              ) : (
                <button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className={`w-full h-11 rounded-full font-extrabold text-xs tracking-wide flex items-center justify-center gap-2 active:scale-[0.98] transition-all shadow-xl ${
                    isSubmitting
                      ? 'bg-white/10 text-white/40 cursor-not-allowed'
                      : listingType === 'auction'
                      ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white hover:opacity-95'
                      : listingType === 'offers'
                      ? 'bg-gradient-to-r from-violet-600 to-purple-500 text-white hover:opacity-95'
                      : 'bg-gradient-to-r from-[#3390EC] to-[#007AFF] text-white hover:opacity-95'
                  }`}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      <span>{t('listingEllipsis')}</span>
                    </>
                  ) : (
                    <>
                      <Store size={15} />
                      <span>
                        {listingType === 'direct'
                          ? t('listForDirectSale')
                          : listingType === 'auction'
                          ? t('startAuction')
                          : t('acceptOffers')}
                      </span>
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default PushMarketModal;
